import {
  type CustomEditor,
  type CustomElement,
  type CustomLeaf,
  type CustomSlateNode,
  extendedNodeTypes,
  getInitialValue,
  htmlToSlateNodes,
  stylesAttributes,
} from '@mint-lib/tw-components';
import DOMPurify from 'dompurify';
import { BaseText, Editor, Element, Node, Text, Transforms } from 'slate';

export const withNormalization = (editor: Editor) => {
  const { normalizeNode } = editor;

  editor.normalizeNode = (entry) => {
    const [node, path] = entry;

    if (Element.isElementType(node, 'list-item')) {
      const isEmpty = editor.children.every(
        (child) => Node.string(child).trim() === '',
      );

      const text = Node.string(node);
      if (text.trim() === '' && isEmpty) {
        Transforms.removeNodes(editor, { at: path });
        editor.children = getInitialValue();
        return;
      }
    }

    // Call the original `normalizeNode` to handle other cases
    normalizeNode(entry);
  };

  return editor as CustomEditor;
};

const ELEMENT_NODE = 1;
const TEXT_NODE = 3;

const INLINE_TAGS = new Set([
  'A',
  'ABBR',
  'B',
  'BR',
  'CITE',
  'CODE',
  'DEL',
  'EM',
  'FONT',
  'I',
  'INS',
  'KBD',
  'MARK',
  'Q',
  'S',
  'SMALL',
  'SPAN',
  'STRONG',
  'SUB',
  'SUP',
  'U',
]);

const isBlockNode = (node: ChildNode) =>
  node.nodeType === ELEMENT_NODE && !INLINE_TAGS.has(node.nodeName);

// Wraps runs of inline nodes into <p> when they sit next to block siblings,
// dropping runs that are whitespace only (the '\n' between <li>, trailing <br>).
// Slate 0.78 `insertFragment` deletes the empty target block together with
// the inline nodes it just inserted when the fragment also contains blocks.
const wrapInlineRuns = (container: HTMLElement) => {
  const children = Array.from(container.childNodes);
  if (!children.some(isBlockNode)) {
    return;
  }

  let run: ChildNode[] = [];
  const flush = () => {
    if (run.length === 0) return;
    const isBlank = run.every((node) =>
      node.nodeType === TEXT_NODE
        ? (node.textContent ?? '').trim() === ''
        : node.nodeName === 'BR',
    );
    if (isBlank) {
      run.forEach((node) => node.remove());
    } else {
      const p = container.ownerDocument.createElement('p');
      run[0].before(p);
      p.append(...run);
    }
    run = [];
  };

  children.forEach((node) => {
    if (isBlockNode(node)) {
      flush();
    } else {
      run.push(node);
    }
  });
  flush();
};

// Browsers put <div> around copied lines (Chrome, our own chat message markup),
// but the Slate parser only knows <p>, so <div> lines would collapse into loose text.
export const normalizePastedBody = (body: HTMLElement) => {
  body
    .querySelectorAll('br.Apple-interchange-newline')
    .forEach((br) => br.remove());

  // Deepest first, so nested <div>s resolve before their parents
  Array.from(body.querySelectorAll('div'))
    .reverse()
    .forEach((div) => {
      if (Array.from(div.childNodes).some(isBlockNode)) {
        wrapInlineRuns(div);
        div.replaceWith(...div.childNodes);
        return;
      }

      const p = body.ownerDocument.createElement('p');
      p.append(...div.childNodes);
      div.replaceWith(p);
    });

  body.querySelectorAll('ul, ol, li, blockquote').forEach((el) => {
    wrapInlineRuns(el as HTMLElement);
  });
  wrapInlineRuns(body);
};

export const insertClipboardData = (
  editor: CustomEditor,
  clipboardData: DataTransfer,
) => {
  // Cut/copy inside a Slate editor carries the exact nodes with their marks;
  // the text/html next to it renders marks as inline styles the parser drops
  if (editor.insertFragmentData(clipboardData)) {
    return;
  }

  const sanitizedHtml = DOMPurify.sanitize(clipboardData.getData('text/html'));
  const parsed = new DOMParser().parseFromString(sanitizedHtml, 'text/html');
  normalizePastedBody(parsed.body);
  Transforms.insertFragment(editor, htmlToSlateNodes(parsed.body));
};

const serializeTextNode = (node: CustomSlateNode, type?: string) => {
  const textNode = node as BaseText & CustomLeaf;
  let string = DOMPurify.sanitize(textNode.text).replaceAll('\n', '<br>');
  // Loop through each style attribute and wrap the text in the corresponding HTML tag
  Object.keys(stylesAttributes).forEach((style) => {
    const isAttribute = textNode[style as keyof CustomLeaf];

    if (isAttribute) {
      const assignedStyle =
        stylesAttributes[style as keyof typeof stylesAttributes];
      string = `<${assignedStyle}>${string}</${assignedStyle}>`;
    }
  });
  // Wrap text in <li> only when it sits directly in a list. Inside a
  // 'list-item' the <li> comes from serializeElement; wrapping again nests
  // <li> in <li>, which the HTML parser splits into separate bullets.
  return type === 'bulleted-list' || type === 'numbered-list'
    ? `<li>${string}</li>`
    : string;
};

const getTag = (node: CustomSlateNode) =>
  extendedNodeTypes[node.type as keyof typeof extendedNodeTypes];

const getSeparator = (prev: CustomSlateNode, next: CustomSlateNode) =>
  Text.isText(prev) || Text.isText(next) || getTag(prev) || getTag(next)
    ? ''
    : '<br>';

const serializeElement = (node: CustomSlateNode) => {
  const element = node as CustomElement;
  const childNodes = element.children as CustomSlateNode[];
  const children = childNodes
    .map(
      (child, index) =>
        (index > 0 ? getSeparator(childNodes[index - 1], child) : '') +
        serializeSlateToHtml(child, node.type),
    )
    .join('');

  // prevent adding empty tags to editor
  if (!children) {
    return '';
  }
  const sanitizedChildren = DOMPurify.sanitize(children, {
    ALLOWED_TAGS: [
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'ul',
      'ol',
      'li',
      'blockquote',
      'a',
      'em',
      'del',
      'u',
      'strong',
      'br',
      'hr',
      'p',
    ],
    ALLOWED_URI_REGEXP: /^(https?|mailto):/, // for safe links
  });

  const tag = getTag(node);

  // prevent tags with empty content
  const trimmedElement = DOMPurify.sanitize(sanitizedChildren, {
    ALLOWED_TAGS: [],
  }).trim();

  if (!trimmedElement) {
    return '';
  }

  return tag
    ? `<${tag}>${sanitizedChildren}</${tag}>`
    : DOMPurify.sanitize(sanitizedChildren, { ALLOWED_TAGS: ['br'] }).trim();
};

export const serializeSlateToHtml = (
  node: CustomSlateNode,
  type?: string,
): string => {
  if (Text.isText(node)) {
    return serializeTextNode(node, type);
  }

  return serializeElement(node);
};

const getTextBlocks = (nodes: Node[]): Element[] =>
  nodes.flatMap((node) => {
    if (!Element.isElement(node)) return [];
    return node.children.some((child) => Text.isText(child))
      ? [node]
      : getTextBlocks(node.children);
  });

// Shift+Enter splits the text into sibling blocks instead of inserting '\n'
export const countPromptLength = (nodes: Node[]): number => {
  const blocks = getTextBlocks(nodes);
  const textLength = blocks.reduce(
    (acc, block) => acc + Node.string(block).length,
    0,
  );

  return textLength + Math.max(blocks.length - 1, 0);
};
